/**
 * Field-type + size-unit metadata — PIM Wave P1 Task 3.
 *
 * `FIELD_TYPE_META` is the ordered "7 types" catalogue for the create-mode
 * type picker (radio-cards) and for rendering a type's GPill label anywhere
 * in the Field Library. i18n keys live under the shared `pim.fieldTypes.*`
 * namespace (brief §13 — "reused later by bindings/preview").
 *
 * `UNIT_KIND_UNITS` mirrors the per-kind allowed-unit lists from
 * `apps/api/garden/features/catalog_schema/units.py` (`UNIT_CONVERSIONS`
 * keys, in file order) verbatim, per brief §10 ("mirror the exact per-kind
 * unit lists... into a local const; comment cites the source file; the
 * reviewer will diff them"). Canonical unit = the factor-1 (base) entry in
 * each kind, listed LAST in the source file's declaration order.
 */
import {
  Calendar,
  Hash,
  ImageIcon,
  List,
  Ruler,
  ToggleLeft,
  Type,
  type LucideIcon,
} from "lucide-react";

import {
  FIELD_TYPE_VALUES,
  UNIT_KIND_VALUES,
  type FieldTypeValue,
  type UnitKindValue,
} from "@/features/admin/attribute-fields/api";

export interface FieldTypeMetaEntry {
  value: FieldTypeValue;
  icon: LucideIcon;
  labelKey: string;
  descriptionKey: string;
}

/** Icon + i18n keys per `FIELD_TYPE_VALUES` (`catalog_schema/models.py`). */
const FIELD_TYPE_ICON: Record<FieldTypeValue, LucideIcon> = {
  text: Type,
  number: Hash,
  boolean: ToggleLeft,
  select: List,
  size: Ruler,
  date: Calendar,
  image: ImageIcon,
};

export const FIELD_TYPE_META: readonly FieldTypeMetaEntry[] = FIELD_TYPE_VALUES.map(
  (value) => ({
    value,
    icon: FIELD_TYPE_ICON[value],
    labelKey: `pim.fieldTypes.${value}.label`,
    descriptionKey: `pim.fieldTypes.${value}.description`,
  }),
);

export function fieldTypeMeta(value: string): FieldTypeMetaEntry | undefined {
  return FIELD_TYPE_META.find((m) => m.value === value);
}

/**
 * Per-kind allowed unit slugs, verbatim from `units.py` `UNIT_CONVERSIONS`
 * (kind -> unit dict key order). The LAST entry in each tuple is the
 * canonical (factor-1) unit for that kind:
 *   length -> m, area -> m2, volume -> l, weight -> kg, count -> pcs.
 */
export const UNIT_KIND_UNITS: Readonly<Record<UnitKindValue, readonly string[]>> = {
  length: ["mm", "cm", "m"],
  area: ["cm2", "m2"],
  volume: ["ml", "l"],
  weight: ["g", "kg"],
  count: ["pcs"],
};

export function allowedUnitsForKind(kind: string): readonly string[] {
  if ((UNIT_KIND_VALUES as readonly string[]).includes(kind)) {
    return UNIT_KIND_UNITS[kind as UnitKindValue];
  }
  return [];
}
